import React, { useEffect, useRef } from 'react';
import './SwingBadge.css';

// Lanyard badge with 2D spring physics (Vercel-badge style): the card
// hangs from an elastic string. Pull it anywhere - the string
// stretches - and on release it springs back, bounces and dangles
// until it settles.
const REST_LEN = 118; // natural string length (px)
const SPRING_K = 160; // string elasticity
const GRAVITY = 2200; // px/s^2
const DAMPING = 1.8; // air resistance

const SwingBadge = () => {
  const pivotRef = useRef(null);
  const stringRef = useRef(null);
  const cardRef = useRef(null);
  const sim = useRef({
    x: 30, // start nudged sideways so it sways on load
    y: REST_LEN,
    vx: 0,
    vy: 0,
    dragging: false,
    grabDX: 0,
    grabDY: 0,
    lastT: null,
    hist: []
  });

  // physics + render loop
  useEffect(() => {
    let raf;
    const tick = (t) => {
      const s = sim.current;
      if (s.lastT == null) s.lastT = t;
      const dt = Math.min((t - s.lastT) / 1000, 0.032);
      s.lastT = t;

      if (!s.dragging) {
        const d = Math.hypot(s.x, s.y) || 0.0001;
        let ax = 0;
        let ay = GRAVITY;
        if (d > REST_LEN) {
          // the string only pulls when taut
          const f = (SPRING_K * (d - REST_LEN)) / d;
          ax -= f * s.x;
          ay -= f * s.y;
        }
        ax -= DAMPING * s.vx;
        ay -= DAMPING * s.vy;
        s.vx += ax * dt;
        s.vy += ay * dt;
        s.x += s.vx * dt;
        s.y += s.vy * dt;
      }

      const dist = Math.hypot(s.x, s.y) || 0.0001;
      const phi = Math.atan2(-s.x, s.y); // rotation that points the string at the card
      if (stringRef.current) {
        stringRef.current.style.height = `${dist}px`;
        stringRef.current.style.transform = `rotate(${phi}rad)`;
      }
      if (cardRef.current) {
        cardRef.current.style.transform = `translate(${s.x}px, ${s.y}px) rotate(${phi}rad)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // drag interaction: the card follows the pointer freely
  useEffect(() => {
    const s = sim.current;

    const onMove = (e) => {
      if (!s.dragging) return;
      const rect = pivotRef.current.getBoundingClientRect();
      s.x = e.clientX - rect.left - s.grabDX;
      s.y = e.clientY - rect.top - s.grabDY;
      s.hist.push({ t: performance.now(), x: s.x, y: s.y });
      if (s.hist.length > 5) s.hist.shift();
    };

    const onUp = () => {
      if (!s.dragging) return;
      s.dragging = false;
      const h = s.hist;
      if (h.length >= 2) {
        const a = h[0];
        const b = h[h.length - 1];
        const dt = (b.t - a.t) / 1000;
        if (dt > 0.001) {
          const cap = (v) => Math.max(-2600, Math.min(2600, v));
          s.vx = cap((b.x - a.x) / dt);
          s.vy = cap((b.y - a.y) / dt);
        }
      }
      s.hist = [];
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    const onDown = (e) => {
      e.preventDefault();
      const rect = pivotRef.current.getBoundingClientRect();
      // keep the point you grabbed under the cursor
      s.grabDX = e.clientX - rect.left - s.x;
      s.grabDY = e.clientY - rect.top - s.y;
      s.dragging = true;
      s.vx = 0;
      s.vy = 0;
      s.hist = [];
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    };

    const card = cardRef.current;
    card?.addEventListener('pointerdown', onDown);
    return () => {
      card?.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  return (
    <div className="swing-badge-pivot" ref={pivotRef}>
      <div className="swing-badge-string" ref={stringRef} />
      <div className="swing-badge-card" ref={cardRef}>
        <div className="swing-badge-clip" />
        <div className="swing-badge-slot" />
        <img className="swing-badge-photo" src="/images/julia.jpg" alt="Julia" draggable="false" />
      </div>
    </div>
  );
};

export default SwingBadge;
